// PHONE SEGMENTED CONTROL — owner task 2026-10-30 (Deals type row + period row).
//
// One grey track (#E9EBEF, radius 11) holding N EQUAL segments; the selected
// segment is white with a soft shadow. A segment is a button with a label and
// an optional second line (the count), OR a caller-supplied node (the year
// select, the 📅 picker) that fills its cell. The control owns no state and no
// handlers: every segment's onClick is the page's own setter, exactly the one
// the chip it replaces called. It adds NO scroll container (no overflow:auto /
// scroll, nothing sticky or fixed) — verify-scroll-ancestors pins that, because
// this sits on the phone touch path where an extra scroller brings back the
// iOS swipe latch.
import React from "react";

export var SEG_TRACK = "#E9EBEF";
export var SEG_RADIUS = 11;
export var SEG_INK = "#1E293B";
export var SEG_MUTED = "#64748B";
export var SEG_SHADOW = "0 1px 3px rgba(15,23,42,0.16), 0 0 0 0.5px rgba(15,23,42,0.04)";

// The cell style a caller-supplied node (SegmentedSelect, the 📅 picker) should
// fill — exported so the picker's `segment` variant reads the same numbers.
export var SEG_CELL_STYLE = { minWidth: 0, minHeight: 34, borderRadius: SEG_RADIUS - 2, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" };
export function segOnStyle(on, color) {
  return { background: on ? "#fff" : "transparent", boxShadow: on ? SEG_SHADOW : "none", color: on ? (color || SEG_INK) : SEG_MUTED, fontWeight: on ? 700 : 600 };
}

// items: [{ key, label, sub?, active?, onClick, color?, title?, dot? } | { key, node }]
// A falsy item is dropped (a control the role does not have leaves no hole).
export function SegmentedControl(p) {
  var items = (p.items || []).filter(Boolean);
  if (!items.length) return null;
  return <div data-seg="1" role="group" aria-label={p.label} style={Object.assign({
      display: "grid", gridTemplateColumns: "repeat(" + items.length + ", minmax(0, 1fr))", gap: 2, padding: 3,
      background: SEG_TRACK, borderRadius: SEG_RADIUS, boxSizing: "border-box", width: "100%" }, p.style || {})}>
    {items.map(function (it) {
      if (it.node) return <div key={it.key} data-seg-cell="1" style={Object.assign({}, SEG_CELL_STYLE)}>{it.node}</div>;
      var on = !!it.active;
      return <button key={it.key} type="button" onClick={it.onClick} title={it.title} data-seg-item="1" data-on={on ? "1" : "0"}
        style={Object.assign({ border: "none", padding: "5px 2px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, lineHeight: 1.2,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, position: "relative", minHeight: 0, maxHeight: "none" },
          SEG_CELL_STYLE, segOnStyle(on, it.color))}>
        <span style={{ maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</span>
        {it.sub !== undefined && it.sub !== null && <span data-seg-sub="1" style={{ fontSize: 11, fontWeight: 600, opacity: on ? 0.8 : 1 }}>{it.sub}</span>}
        {it.dot && <span title={it.dotTitle} style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: "#22C55E" }}/>}
      </button>;
    })}
  </div>;
}

// A native <select> drawn as a segment ("2026 ▾"): the caller's value/onChange/
// options verbatim — this is the Deals year picker's handler, not a copy of it.
export function SegmentedSelect(p) {
  return <span data-seg-select="1" style={{ position: "relative", display: "flex", width: "100%", minWidth: 0 }}>
    <select value={p.value} onChange={p.onChange} title={p.title} aria-label={p.title}
      style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none", width: "100%", minWidth: 0, border: "none", background: "transparent",
        fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: SEG_MUTED, textAlign: "center", textAlignLast: "center", padding: "5px 10px 5px 2px",
        borderRadius: SEG_RADIUS - 2, cursor: "pointer", minHeight: 34, lineHeight: 1.2 }}>
      {(p.options || []).map(function (o) { return <option key={String(o.value)} value={String(o.value)}>{o.label}</option>; })}
    </select>
    <span aria-hidden="true" style={{ position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: SEG_MUTED, pointerEvents: "none" }}>▾</span>
  </span>;
}
